import { useEffect } from 'react';

type NodeMapping = { node: Text; start: number; end: number };
/** Text of a DOM subtree with letters and digits only, lower-cased, mapped back to its text nodes. */
type IndexedText = { normalized: string; originalStarts: number[]; originalEnds: number[]; nodeMapping: NodeMapping[] };
type Segment = { text: IndexedText; start: number; end: number };

const HIGHLIGHT_ATTR = 'data-citation-highlight';
/** Shortest part of an extract, in letters and digits, that a page split will accept. */
const MIN_PART_LENGTH = 4;

const isLetterOrNumber = (char: string) => /[\p{L}\p{N}]/u.test(char);
// Lower-cased a character at a time so the query and `indexText` always agree.
const normalizeForSearch = (str: string) =>
  Array.from(str)
    .filter(isLetterOrNumber)
    .map((char) => char.toLowerCase())
    .join('');

/** Whether `text` contains `query`, comparing letters and digits only (same as `performDomTextSearch`). */
export const containsSearchText = (text: string, query: string): boolean => {
  const normalizedQuery = normalizeForSearch(query);
  if (!normalizedQuery) return false;
  return normalizeForSearch(text).includes(normalizedQuery);
};

const clearHighlights = (container: HTMLElement) => {
  container.querySelectorAll<HTMLElement>(`[${HIGHLIGHT_ATTR}]`).forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
};

const indexText = (root: Element): IndexedText => {
  let fullText = '';
  const nodeMapping: NodeMapping[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    const textValue = node.nodeValue || '';
    if (!textValue.trim()) continue;
    const start = fullText.length;
    fullText += textValue;
    nodeMapping.push({ node: node as Text, start, end: fullText.length });
    fullText += ' ';
  }

  let normalized = '';
  const originalStarts: number[] = [];
  const originalEnds: number[] = [];
  let index = 0;
  for (const char of fullText) {
    if (isLetterOrNumber(char)) {
      // A lower-cased char can be longer than the original, so map each of its units.
      const lowered = char.toLowerCase();
      normalized += lowered;
      for (let i = 0; i < lowered.length; i++) {
        originalStarts.push(index);
        originalEnds.push(index + char.length);
      }
    }
    index += char.length;
  }
  return { normalized, originalStarts, originalEnds, nodeMapping };
};

/**
 * Wraps a portion of a single text node in a highlight <mark>.
 * Uses splitText so the parent element structure is never broken.
 */
const wrapTextRange = (textNode: Text, startOffset: number, endOffset: number): HTMLElement | null => {
  try {
    const clampedEnd = Math.min(endOffset, textNode.length);
    const clampedStart = Math.min(startOffset, clampedEnd);

    let target: Text = textNode;

    if (clampedStart > 0) {
      target = textNode.splitText(clampedStart);
    }

    if (clampedEnd - clampedStart < target.length) {
      target.splitText(clampedEnd - clampedStart);
    }

    const mark = document.createElement('mark');
    mark.setAttribute(HIGHLIGHT_ATTR, 'true');
    // Same highlight as chat search.
    mark.style.backgroundColor = 'color-mix(in srgb, var(--color-theme-search-highlight) 50%, transparent)';
    mark.style.color = 'inherit';
    mark.style.borderRadius = '2px';

    target.parentNode!.replaceChild(mark, target);
    mark.appendChild(target);
    return mark;
  } catch {
    return null;
  }
};

/** Highlights the normalized range [start, end) and returns the first mark. */
const highlightSegment = ({ text, start, end }: Segment): HTMLElement | null => {
  const originalStart = text.originalStarts[start];
  const originalEnd = text.originalEnds[end - 1];

  let firstMark: HTMLElement | null = null;
  for (const info of text.nodeMapping) {
    if (info.start >= originalEnd || info.end <= originalStart) continue;
    const localStart = Math.max(0, originalStart - info.start);
    const localEnd = Math.min(info.end - info.start, originalEnd - info.start);
    const mark = wrapTextRange(info.node, localStart, localEnd);
    if (mark && !firstMark) firstMark = mark;
  }
  return firstMark;
};

/** Length of the longest prefix of `query` that occurs in `text` (containment shrinks with length). */
const longestContainedPrefix = (text: string, query: string): number => {
  let low = 0;
  let high = query.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (text.includes(query.slice(0, mid))) low = mid;
    else high = mid - 1;
  }
  return low;
};

/**
 * Matches a query that runs off the end of one page onto the next. The text between the two parts
 * (a footer, a header, a page number) may be missing from the query, so each page is searched on
 * its own: the longest leading part of the query on the first page, the rest on the second.
 */
const findAcrossPages = (first: IndexedText, second: IndexedText, query: string): Segment[] | null => {
  const firstLength = longestContainedPrefix(first.normalized, query);
  const remaining = query.slice(firstLength);
  // A part this short would match almost anywhere, e.g. a list number on the wrong page.
  if (firstLength < MIN_PART_LENGTH || remaining.length < MIN_PART_LENGTH) return null;

  // The extract ends at the bottom of the first page and carries on at the top of the second.
  const firstStart = first.normalized.lastIndexOf(query.slice(0, firstLength));
  const secondStart = second.normalized.indexOf(remaining);
  if (secondStart === -1) return null;

  return [
    { text: first, start: firstStart, end: firstStart + firstLength },
    { text: second, start: secondStart, end: secondStart + remaining.length },
  ];
};

const findInPages = (rootNode: HTMLElement, query: string, pageNumber?: number): Segment[] | null => {
  const pages = new Map<number, IndexedText>();
  rootNode.querySelectorAll('[data-page-number]').forEach((element) => {
    const number = Number(element.getAttribute('data-page-number'));
    if (Number.isInteger(number)) pages.set(number, indexText(element));
  });

  // The cited page first, then the one before it in case the backend cited where the extract ends.
  const preferred = pageNumber ? [pageNumber, pageNumber - 1] : [];
  const startPages = new Set([...preferred, ...[...pages.keys()].sort((a, b) => a - b)]);

  for (const start of startPages) {
    const first = pages.get(start);
    const second = pages.get(start + 1);
    if (!first || !second) continue;
    const segments = findAcrossPages(first, second, query);
    if (segments) return segments;
  }
  return null;
};

export const performDomTextSearch = (
  searchText: string,
  containerId = 'pdf-viewer-container',
  pageNumber?: number,
): boolean => {
  const rootNode = document.getElementById(containerId);
  if (!rootNode) return false;

  clearHighlights(rootNode);

  const normalizedQuery = normalizeForSearch(searchText);
  if (!normalizedQuery) return false;

  let segments: Segment[] | null = null;
  const whole = indexText(rootNode);
  const matchIndex = whole.normalized.indexOf(normalizedQuery);
  if (matchIndex !== -1) {
    segments = [{ text: whole, start: matchIndex, end: matchIndex + normalizedQuery.length }];
  } else {
    segments = findInPages(rootNode, normalizedQuery, pageNumber);
  }
  if (!segments) return false;

  try {
    let firstMark: HTMLElement | null = null;
    for (const segment of segments) {
      const mark = highlightSegment(segment);
      if (mark && !firstMark) firstMark = mark;
    }

    firstMark?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return !!firstMark;
  } catch (e) {
    console.error('Error highlighting citation text:', e);
  }

  return false;
};

export const usePdfTextSearch = (
  searchText?: string,
  pageNumber?: number,
  rerunKey?: unknown,
  containerId?: string,
) => {
  useEffect(() => {
    if (!searchText) return;

    window.getSelection()?.removeAllRanges();

    let attempts = 0;
    const maxAttempts = 10;
    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    const attemptSelection = () => {
      if (cancelled) return;
      if (performDomTextSearch(searchText, containerId, pageNumber)) return;

      attempts++;
      if (attempts >= maxAttempts) {
        return;
      }

      timeoutId = setTimeout(attemptSelection, 500);
    };

    timeoutId = setTimeout(attemptSelection, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchText, pageNumber, rerunKey, containerId]);
};
